/**
 * Convert WordPress PHP date format to JavaScript compatible format
 *
 * @param {string} phpFormat - WordPress PHP date format
 * @param {Date} date - JavaScript Date object
 * @returns {string} Formatted date string
 */
export function formatDate(date, phpFormat = 'F j, Y') {
  // Convert PHP date format to JS
  const formatMap = {
    // Day
    'd': (date) => padZero(date.getDate()),           // Day of the month with leading zeros (01-31)
    'j': (date) => date.getDate(),                    // Day of the month without leading zeros (1-31)
    'D': (date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()], // Short day name (Mon-Sun)
    'l': (date) => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()], // Full day name

    // Month
    'm': (date) => padZero(date.getMonth() + 1),     // Month with leading zeros (01-12)
    'n': (date) => date.getMonth() + 1,              // Month without leading zeros (1-12)
    'M': (date) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()], // Short month name
    'F': (date) => ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][date.getMonth()], // Full month name

    // Year
    'Y': (date) => date.getFullYear(),               // Full year (e.g., 2023)
    'y': (date) => date.getFullYear().toString().slice(-2), // Two digit year (e.g., 23)

    // Time
    'g': (date) => date.getHours() % 12 || 12,       // 12-hour format without leading zeros (1-12)
    'h': (date) => padZero(date.getHours() % 12 || 12), // 12-hour format with leading zeros (01-12)
    'G': (date) => date.getHours(),                  // 24-hour format without leading zeros (0-23)
    'H': (date) => padZero(date.getHours()),         // 24-hour format with leading zeros (00-23)
    'i': (date) => padZero(date.getMinutes()),       // Minutes with leading zeros (00-59)
    's': (date) => padZero(date.getSeconds()),       // Seconds with leading zeros (00-59)
    'a': (date) => date.getHours() < 12 ? 'am' : 'pm', // Lowercase am/pm
    'A': (date) => date.getHours() < 12 ? 'AM' : 'PM', // Uppercase AM/PM
  };

  // Helper function to pad with zeros
  function padZero(num) {
    return num.toString().padStart(2, '0');
  }

  // Process the format string
  let result = '';
  let escaping = false;

  for (let i = 0; i < phpFormat.length; i++) {
    const char = phpFormat[i];

    // Handle escape character
    if (char === '\\') {
      escaping = true;
      continue;
    }

    // If we're escaping, add the character as-is
    if (escaping) {
      result += char;
      escaping = false;
      continue;
    }

    // Check if we have a format specifier
    if (formatMap[char]) {
      result += formatMap[char](date);
    } else {
      // Not a format specifier, add as-is
      result += char;
    }
  }

  return result;
}

/**
 * Format date and time according to WordPress settings
 *
 * @param {string|Date} dateString - Date string or object to format
 * @param {string} dateFormat - WordPress date format
 * @param {string} timeFormat - WordPress time format
 * @param {boolean} includeTime - Whether to include time in the output
 * @returns {string} Formatted date and time string
 */
export function formatDateTime(dateString, dateFormat, timeFormat, includeTime = false) {
  if (!dateString) return '';

  const date = dateString instanceof Date ? dateString : new Date(dateString);

  // Handle invalid dates
  if (isNaN(date.getTime())) return '';

  // Use default formats if not provided
  const format = includeTime
    ? `${dateFormat || 'F j, Y'} ${timeFormat || 'g:i a'}`
    : (dateFormat || 'F j, Y');

  return formatDate(date, format);
}

/**
 * Format time in MM:SS format
 *
 * @param {number|null} timeLeft - Time in seconds
 * @returns {string} Formatted time string in MM:SS format
 */
export function formatTime(timeLeft) {
  if (timeLeft === null) return '';
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}